import { useResizeObserver } from '@react-aria/utils';
import * as React from 'react';

/**
 * A helper function that returns whether the element is overflowing.
 *
 * @param {HTMLElement} element
 *
 * @returns boolean
 */
function checkOverflow(element?: HTMLElement | null, maxLines = 1) {
  if (!element) return false;

  const ignoreHeightOverflow = maxLines === 1;

  const curOverflow = element.style.overflow;

  if (!curOverflow || curOverflow === 'visible') element.style.overflow = 'hidden';

  const isOverflowing =
    element.clientWidth < element.scrollWidth || (!ignoreHeightOverflow && element.clientHeight < element.scrollHeight);

  return isOverflowing;
}

/**
 * A hook that returns whether the element is overflowing.
 *
 * @param {React.RefObject<HTMLElement>} ref
 */
export function useIsOverflowing(ref: React.RefObject<HTMLElement | null>, maxLines = 1): boolean {
  const [isOverflowing, setIsOverflowing] = React.useState(checkOverflow(ref?.current));

  const handleResize = React.useCallback(() => {
    setIsOverflowing(checkOverflow(ref?.current, maxLines));
  }, [ref, maxLines]);

  useResizeObserver({
    ref,
    onResize: handleResize,
  });

  return isOverflowing;
}
